import { FormikProps } from 'formik';
import { FC, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PrimaryButton from 'components/ui/PrimaryButton';
import Container from 'components/ui/Container';
import { IProfile } from './schema';
import { Avatar } from 'common/constant';
import { useListInternalUserDetailsQuery } from 'redux/reducers/internal-user';
import { useListOrganizationCentreQuery } from 'redux/reducers/organizations-centre';
import { useListOrganizationDepartmentQuery } from 'redux/reducers/organization-department';
import ReactToPrint from 'react-to-print';
import { backendURL } from 'redux/reducers/settings';

interface IProfileForm {
  formik?: FormikProps<IProfile>;
}

const getImage = (image: File) => {
  try {
    return URL.createObjectURL(image);
  } catch (error) {
    return '';
  }
};

const InternalUser: FC<IProfileForm> = ({ formik }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data } = useListInternalUserDetailsQuery(id);
  const { data: organizationcenter } = useListOrganizationCentreQuery();
  const { data: organizationdepartment } = useListOrganizationDepartmentQuery();
const internaluser=data?.data
  const ref = useRef<HTMLDivElement | null>(null);

  // Helper component for consistent data display
  const DataItem = ({ label, value }: { label: string; value: any }) => (
    <div className="col-sm-6 col-md-4 col-lg-3 mb-3">
      <label className="text-muted small fw-bold text-uppercase d-block mb-1">{label}</label>
      <span className="text-dark fw-medium">{value || 'N/A'}</span>
    </div>
  );

  return (
    <Container title="Profile">
      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
        <h4 className="fw-bold mb-0">Profile Information</h4>
        <div className="d-flex gap-2">
          <ReactToPrint
            content={() => ref.current}
            trigger={() => <button className="btn btn-outline-primary px-4">Print Profile</button>}
          />
          <PrimaryButton
            title="Edit Profile"
            type="button"
            onClick={() => navigate(`/internal-user-management/internal/edit/${id}`)}
          />
        </div>
      </div>

      <div ref={ref} className="bg-white p-2 p-md-4 rounded shadow-sm">
        {/* Profile Image Card */}
        <div className="card border-0 bg-light mb-4">
          <div className="card-body text-center">
            <div className="position-relative d-inline-block">
              <img
                src={
                  internaluser?.image
                    ? typeof internaluser?.image === 'string'
                      ? backendURL+ internaluser?.image
                      : getImage(internaluser?.image)
                    : internaluser?.image || Avatar
                }
                className="rounded-circle shadow-sm border border-4 border-white"
                alt="user-profile"
                style={{ width: '120px', height: '120px', objectFit: 'cover' }}
              />
            </div>
            <h5 className="mt-3 mb-0 fw-bold">
              {internaluser?.first_name} {internaluser?.last_name}
            </h5>
            <p className="text-muted small">{internaluser?.email}</p>
          </div>
        </div>

        {/* Personal Information */}
        <section className="mb-5">
          <h6 className="text-primary fw-bold border-bottom pb-2 mb-3">PERSONAL INFORMATION</h6>
          <div className="row">
            <DataItem label="First Name" value={internaluser?.first_name} />
            <DataItem label="Middle Name" value={internaluser?.middle_name} />
            <DataItem label="Last Name" value={internaluser?.last_name} />
            <DataItem label="Email" value={internaluser?.email} />
            <DataItem label="PAN Number" value={internaluser?.pan_number} />
            <DataItem label="Citizenship No." value={internaluser?.citizenship_num} />
            <DataItem label="Gender" value={internaluser?.gender === 1 ? 'Male' : 'Female'} />
            <DataItem label="Passport No." value={internaluser?.passport_num} />
            <DataItem label="Marital Status" value={internaluser?.marital_status === 1 ? 'Single' : 'Married'} />
            <DataItem label="Date of Birth" value={internaluser?.dob_AD} />
            <DataItem label="Blood Group" value={internaluser?.blood_group} />
            <DataItem label="Religion" value={internaluser?.religion} />
            <DataItem label="Mobile Number" value={internaluser?.mobile_number} />
          </div>
        </section>

        {/* Family Information */}
        <section className="mb-5">
          <h6 className="text-primary fw-bold border-bottom pb-2 mb-3">FAMILY DETAILS</h6>
          <div className="row">
            <DataItem label="Father's Name" value={internaluser?.father_name} />
            <DataItem label="Mother's Name" value={internaluser?.mother_name} />
            <DataItem label="Grandfather's Name" value={internaluser?.grandfather_name} />
            <DataItem label="Grandmother's Name" value={internaluser?.grandmother_name} />
          </div>
        </section>

        {/* Address Details */}
        <section className="mb-5">
          <h6 className="text-primary fw-bold border-bottom pb-2 mb-3">ADDRESS DETAILS</h6>
          <div className="row g-4">
            <div className="col-md-6">
              <div className="p-3 border rounded bg-light">
                <p className="fw-bold mb-3 small text-primary">TEMPORARY ADDRESS</p>
                <div className="row g-2">
                  <DataItem label="Province" value={internaluser?.temporary_address?.province?.name_en} />
                  <DataItem label="District" value={internaluser?.temporary_address?.district?.name_en} />
                  <DataItem label="Ward No." value={internaluser?.temporary_address?.ward_no} />
                  <DataItem label="Street" value={internaluser?.temporary_address?.tole_street} />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-3 border rounded bg-light">
                <p className="fw-bold mb-3 small text-primary">PERMANENT ADDRESS</p>
                <div className="row g-2">
                  <DataItem label="Province" value={internaluser?.permanent_address?.province?.name_en} />
                  <DataItem label="District" value={internaluser?.permanent_address?.district?.name_en} />
                  <DataItem label="Ward No." value={internaluser?.permanent_address?.ward_no} />
                  <DataItem label="Street" value={internaluser?.permanent_address?.tole_street} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section className="mb-5">
          <h6 className="text-primary fw-bold border-bottom pb-2 mb-3">PROFESSIONAL EXPERIENCE</h6>
          <div className="row">
            {internaluser?.inhouse_experience?.map((item: any, index: number) => (
              <div key={index} className="col-md-6 mb-3">
                <div className="card card-body border-start border-4 border-primary shadow-sm">
                  <div className="row g-2">
                    <DataItem 
                       label="Department" 
                       value={organizationdepartment?.data.find((d: any) => d.tms_department_id == item.department)?.name} 
                    />
                    <DataItem 
                       label="Center" 
                       value={organizationcenter?.data.find((c: any) => c.tms_center_id == item.center)?.name} 
                    />
                    <DataItem label="Position" value={item?.start_position} />
                    <DataItem label="Duration" value={`${item?.date_from} to ${item?.date_to || 'Present'}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Education Section */}
        <section className="mb-3">
          <h6 className="text-primary fw-bold border-bottom pb-2 mb-3">EDUCATION</h6>
          <div className="row">
            {internaluser?.education?.map((edu: any, index: number) => (
              <div key={index} className="col-md-4 mb-3">
                <div className="p-3 border rounded shadow-sm h-100">
                  <div className="fw-bold text-dark">{edu?.degree}</div>
                  <div className="small text-muted mb-2">{edu?.level}</div>
                  <div className="small text-dark"><strong>Institution:</strong> {edu?.institution_name}</div>
                  <div className="small text-dark"><strong>Score:</strong> {edu?.percentage}%</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Container>
  );
};

export default InternalUser;